/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { lazy, Suspense } from 'react';

import { cssMap, jsx } from '@compiled/react';
// View switcher disabled — see comments below. Kept commented so re-enabling
// the `twg_microsite_view_switcher`-gated `SwitchViewBanner` UI is a one-line
// edit (the atom + jotai usage are otherwise unreferenced here).
// import { useAtom } from 'jotai';

import { SessionTracker } from '../../../common/analytics/SessionTracker';
import { useAtlassianUserContext } from '../../../hooks/use-user-context';
import { CollectFeedback } from '../../components/CollectFeedback';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { LoadingView } from '../../components/LoadingView';
// import { forceLoggedOutViewAtom } from '../../state';

/**
 * Lazy-loaded gating component that resolves the user's `cloudId` and primes
 * the shared atoms before the logged-in view mounts. Code-split out so the
 * App Switcher / cloudId resolution logic and the heavy LoggedInView modules
 * are only fetched once we know the user is authenticated.
 */
const LoggedInGate = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlassian/twg-microsite-experience_logged-in-gate" */ './LoggedInGate'
		),
);

/**
 * Lazy-loaded marketing/logged-out experience. Code-split out so the
 * landing-page assets are only fetched when the visitor is unauthenticated
 * (or has toggled the dev "Logged Out View" switcher).
 */
const LoggedOutView = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/twg-microsite-experience_logged-out-view" */ './components/LoggedOutView'
	).then((m) => ({ default: m.LoggedOutView })),
);

const styles = cssMap({
	loadingContainer: {
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		backgroundColor: '#111213',
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '100vh',
		WebkitFontSmoothing: 'antialiased',
		MozOsxFontSmoothing: 'grayscale',
	},
});

const LoadingFallback = (): JSX.Element => (
	<div css={styles.loadingContainer}>
		<LoadingSpinner size="large" />
	</div>
);

export default function Home(): React.JSX.Element | null {
	const { loading, isAuthed, isBlocked, identity } = useAtlassianUserContext();
	// View switcher disabled — the `twg_microsite_view_switcher`-gated
	// `SwitchViewBanner` is the only UI that flips this atom, and the
	// forced-login flow that motivated the switcher has been removed. Kept
	// commented out so re-enabling both gates is a one-line edit.
	// const [forceLoggedOut] = useAtom(forceLoggedOutViewAtom);

	// Defensive gate: if the access-control FG check has decided this user
	// isn't allowed to view the microsite, render nothing. This guarantees
	// neither the `LoggedInGate` nor `LoggedOutView` lazy chunk will be
	// requested over the network, even if `loading` has flipped to `false`
	// before `UserProvider`'s own short-circuit kicks in. Belt-and-braces
	// with the `isBlocked` short-circuit in `UserProvider`.
	if (isBlocked) {
		return null;
	}

	if (loading) {
		return <LoadingView />;
	}

	// `forceLoggedOut ||` removed from the condition — see commented atom
	// declaration above.
	if (!isAuthed) {
		return (
			<React.Fragment>
				<SessionTracker authState="loggedOut" />
				<Suspense fallback={<LoadingFallback />}>
					<LoggedOutView />
				</Suspense>
				<CollectFeedback
					atlassianAccountId=""
					entrypointId="5cc48fe0-977e-45ce-9c7d-bbd5ee4a00ce"
					name={identity.name ?? undefined}
					url={window.location.href}
					locale={'en'}
				/>
			</React.Fragment>
		);
	}

	// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
	return (
		<React.Fragment>
			<SessionTracker authState="loggedIn" />
			<Suspense fallback={<LoadingView />}>
				<LoggedInGate />
			</Suspense>
			<CollectFeedback
				atlassianAccountId={identity.accountId}
				entrypointId="5cc48fe0-977e-45ce-9c7d-bbd5ee4a00ce"
				name={identity.name}
				url={window.location.href}
				locale={'en'}
			/>
		</React.Fragment>
	);
}
